import { cn } from "@/shared/lib/utils";
import {
  predictVoiceoverCap,
  voiceoverFixAdvice,
  type VoiceoverCapPrediction,
} from "@/shared/lib/video/voiceoverCap";

/**
 * 口播「念得下多少字」实时提示 + 将被截掉的句子原文(1007)。
 *
 * 1007 测试机:10 秒镜写了 91 字口播,生成时按时长截掉了尾句,老板没注意到提交时那条小字,
 * 看成片以为「后面没字幕」。所以:
 *   · 输入框下方常显「约可念 N 字 · 当前 M 字」,超了换警示色;
 *   · 超了就**逐句写出会被截掉的原文**,并告诉用户怎么办(缩短口播 / 拉长时长);
 *   · 只提示不拦提交(截掉是服务端的确定行为,知情即可)。
 * 字数/裁点全部来自 `predictVoiceoverCap`(先清洗再截断,与服务端同口径),这里只负责说人话。
 */

/** 被截掉的句子逐条列出(给输入框下方与提交确认处共用)。 */
export function VoiceoverDroppedList({
  prediction,
  className,
}: {
  prediction: VoiceoverCapPrediction;
  className?: string;
}) {
  if (!prediction.cut) return null;
  const items =
    prediction.droppedSentences.length > 0 ? prediction.droppedSentences : [prediction.dropped];
  return (
    <ul data-testid="voiceover-cap-dropped" className={cn("space-y-0.5", className)}>
      {items.map((s, i) => (
        <li key={i} className="break-all font-medium text-foreground">
          「{s}」
        </li>
      ))}
    </ul>
  );
}

/**
 * 输入框下方的实时提示。text 为空或时长未知时不渲染。
 * `subject` 用来指代时长的主语,例如「本镜」「这条视频」。
 */
export function VoiceoverCapHint({
  text,
  durationSec,
  subject = "这段",
  className,
}: {
  text: string | null | undefined;
  durationSec: number | null | undefined;
  subject?: string;
  className?: string;
}) {
  const p = predictVoiceoverCap(text, durationSec);
  if (!p.cleaned || p.limit <= 0) return null;
  const raw = (text ?? "").trim();
  const normalized = raw !== p.cleaned;
  return (
    <div
      data-testid="voiceover-cap-hint"
      data-cut={p.cut ? "true" : "false"}
      className={cn(
        "mt-1 text-2xs leading-relaxed",
        p.cut
          ? "rounded-md border border-amber-400/60 bg-amber-50/70 px-2 py-1.5 text-amber-800 dark:border-amber-400/40 dark:bg-amber-950/30 dark:text-amber-300"
          : "text-muted-foreground",
        className
      )}
    >
      <p className={cn("tabular-nums", p.cut && "font-medium")}>
        {subject} {durationSec} 秒约可念 {p.limit} 字 · 当前 {p.cleaned.length} 字
        {normalized ? "(引号、换行不计)" : ""}
        {p.cut ? ` · 超出 ${p.cleaned.length - p.limit} 字` : ""}
      </p>
      {p.cut && (
        <>
          <p className="mt-1">下面这些内容不会念出来,也不会出字幕:</p>
          <VoiceoverDroppedList prediction={p} className="mt-0.5" />
          <p className="mt-1">{voiceoverFixAdvice(p)}</p>
        </>
      )}
    </div>
  );
}

/**
 * 提交按钮旁 / 确认弹层里的再提醒(只在会被截时渲染)。
 */
export function VoiceoverCutSubmitNotice({
  text,
  durationSec,
  className,
}: {
  text: string | null | undefined;
  durationSec: number | null | undefined;
  className?: string;
}) {
  const p = predictVoiceoverCap(text, durationSec);
  if (!p.cut) return null;
  return (
    <div
      data-testid="voiceover-cut-submit-notice"
      className={cn(
        "rounded-md border border-amber-400/60 bg-amber-50/70 px-2.5 py-1.5 text-2xs leading-relaxed text-amber-800 dark:border-amber-400/40 dark:bg-amber-950/30 dark:text-amber-300",
        className
      )}
    >
      <p className="font-medium">口播太长,以下内容不会念出也不会出字幕:</p>
      <VoiceoverDroppedList prediction={p} className="mt-0.5" />
      <p className="mt-0.5">{voiceoverFixAdvice(p)}仍可直接提交。</p>
    </div>
  );
}
